import { createAsyncThunk } from '@reduxjs/toolkit'
import { doc, setDoc } from 'firebase/firestore'
import { db } from '@/services/firebase/config'
import { firestoreService, getColombianTimestamp } from '@/services/firebase/firestore.service'
import { CarouselConfig, CarouselItem } from '@/types/carousel.types'
import { RootState } from '@/app/store'

const CAROUSEL_COLLECTION = 'carouselConfig'
const CAROUSEL_DOCUMENT = 'main'

/**
 * Lee la configuración del carrusel desde `carouselConfig/main` y devuelve
 * únicamente los ítems dinámicos. Si el documento no existe (primer arranque),
 * resuelve con `[]` para que el carrusel muestre solo el ítem fijo.
 */
export const fetchCarouselConfig = createAsyncThunk<CarouselItem[], void, { rejectValue: string }>(
  'carousel/fetchCarouselConfig',
  async (_, { rejectWithValue }) => {
    try {
      const config = (await firestoreService.getDocument(
        CAROUSEL_COLLECTION,
        CAROUSEL_DOCUMENT
      )) as (CarouselConfig & { id: string }) | null

      if (!config || !Array.isArray(config.items)) return []
      return config.items
    } catch (error: any) {
      console.error('Error fetching carousel config:', error)
      return rejectWithValue(
        error.message || 'No se pudo cargar la configuración del carrusel.'
      )
    }
  }
)

export interface SaveCarouselConfigResponse {
  items: CarouselItem[]
  updatedAt: string
  updatedBy: string
}

/**
 * Persiste únicamente los ítems dinámicos en `carouselConfig/main`. El ítem fijo
 * (`STATIC_CAROUSEL_ITEM`) nunca se incluye en el payload: el thunk filtra de forma
 * defensiva cualquier ítem con `fixed === true` antes de `setDoc`.
 */
export const saveCarouselConfig = createAsyncThunk<
  SaveCarouselConfigResponse,
  CarouselItem[],
  { rejectValue: string; state: RootState }
>(
  'carousel/saveCarouselConfig',
  async (items, { rejectWithValue, getState }) => {
    try {
      const state = getState()
      const currentUser = state.auth.authUser
      if (!currentUser?.uid) {
        return rejectWithValue('Sesión no válida. Por favor vuelve a iniciar sesión.')
      }

      // Defensive filter: el ítem fijo NUNCA se persiste en Firestore.
      const dynamicItems: CarouselItem[] = items
        .filter((item) => !item.fixed)
        .map((item, i) => ({
          mediaId: item.mediaId,
          name: item.name,
          url: item.url,
          type: item.type,
          order: i,
          fitMode: item.fitMode ?? 'cover',
        }))

      const updatedAtDate = getColombianTimestamp()
      const payload: CarouselConfig = {
        id: CAROUSEL_DOCUMENT,
        items: dynamicItems,
        updatedAt: updatedAtDate.toISOString(),
        updatedBy: currentUser.uid,
      }

      await setDoc(doc(db, CAROUSEL_COLLECTION, CAROUSEL_DOCUMENT), {
        ...payload,
        updatedAt: updatedAtDate,
      })

      return {
        items: dynamicItems,
        updatedAt: payload.updatedAt,
        updatedBy: payload.updatedBy,
      }
    } catch (error: any) {
      console.error('Error saving carousel config:', error)
      return rejectWithValue(
        error.message || 'No se pudo guardar la configuración del carrusel.'
      )
    }
  }
)
